import {
  Dialog,
  DialogTitle,
  Box,
  Typography,
  DialogContent,
  DialogContentText,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  DialogActions,
  Button,
  useMediaQuery,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import { LoadingButton } from "@mui/lab";

const NotaryCancelModal = ({
  handleNotaryCancelConfirm,
  notaryCancelModalOpen,
  handleNotaryCancelClose,
  notaryCancelLoading,
  responseMessage,
  isError,
}) => {
  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  // Notary cancellation confirmation modal state

  return (
    <Dialog
      open={notaryCancelModalOpen}
      onClose={notaryCancelLoading ? () => {} : handleNotaryCancelClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
            boxShadow: 3,
            ...(mobileSize && { width: "100%", m: 1.5 }),
          },
        },
      }}
    >
      <DialogTitle
        sx={{ textAlign: "center", px: mobileSize ? 1.5 : 3, pb: 1 }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <WarningIcon
            sx={{ color: "error.main", fontSize: mobileSize ? 24 : 32 }}
          />
          <Typography
            variant="h5"
            fontWeight="bold"
            color="error.main"
            sx={{ fontSize: mobileSize ? "1.25rem" : "1.5rem" }}
          >
            Cancel Notary Subscription
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: mobileSize ? 1.5 : 3, py: 2 }}>
        <DialogContentText
          sx={{
            mb: 3,
            textAlign: "center",
            fontSize: mobileSize ? "0.9rem" : "1.1rem",
          }}
        >
          Are you sure you want to cancel your notary subscription? This action
          cannot be undone.
        </DialogContentText>

        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          slotProps={{
            icon: { sx: { display: mobileSize ? "none" : "flex" } },
          }}
        >
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Important Information:
          </Typography>
          <List dense>
            <ListItem
              sx={{ px: 0, alignItems: mobileSize ? "start" : "center" }}
            >
              <ListItemIcon
                sx={{
                  minWidth: mobileSize ? "34px" : "56px",
                  mt: mobileSize ? 1 : 0,
                }}
              >
                <MoneyOffIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary="No Refunds Available"
                secondary="If you cancel your notary subscription now, we cannot offer any refunds for the cancellation."
              />
            </ListItem>
            <ListItem
              sx={{ px: 0, alignItems: mobileSize ? "start" : "center" }}
            >
              <ListItemIcon
                sx={{
                  minWidth: mobileSize ? "34px" : "56px",
                  mt: mobileSize ? 1 : 0,
                }}
              >
                <AccessTimeIcon color="warning" />
              </ListItemIcon>
              <ListItemText
                primary="Pending Notarizations"
                secondary="If you have any pending notarizations in progress, the cancellation will take place only when all progress has been completed."
              />
            </ListItem>
            <ListItem
              sx={{ px: 0, alignItems: mobileSize ? "start" : "center" }}
            >
              <ListItemIcon
                sx={{
                  minWidth: mobileSize ? "34px" : "56px",
                  mt: mobileSize ? 1 : 0,
                }}
              >
                <InfoIcon color="info" />
              </ListItemIcon>
              <ListItemText
                primary="24-Hour Grace Period"
                secondary="If there are no pending notarizations, the cancellation will be pending for one day. During this period, you can cancel the cancellation if you change your mind."
              />
            </ListItem>
          </List>
        </Alert>

        <Box
          sx={{
            backgroundColor: "#f5f5f5",
            p: 2,
            borderRadius: 1,
            border: "1px solid #ddd",
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: "italic" }}
          >
            By proceeding with the cancellation, you acknowledge that you have
            read and understood the terms above, including the no-refund policy
            and the cancellation timeline.
          </Typography>
        </Box>
        <Box>
          <Typography align="center" color={isError ? "error" : "success"}>
            {responseMessage}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: mobileSize ? 1.5 : 3,
          pb: mobileSize ? 1.5 : 3,
          gap: mobileSize ? 1 : 2,
          flexWrap: "wrap",
        }}
      >
        <Button
          onClick={handleNotaryCancelClose}
          variant="outlined"
          size={mobileSize ? "small" : "large"}
          sx={{ minWidth: 120 }}
          disabled={notaryCancelLoading}
        >
          Keep Subscription
        </Button>
        <LoadingButton
          loading={notaryCancelLoading}
          onClick={handleNotaryCancelConfirm}
          variant="contained"
          color="error"
          size={mobileSize ? "small" : "large"}
          sx={{ minWidth: 120 }}
        >
          Cancel Subscription
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default NotaryCancelModal;
